import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import LoadingSpinner from '@/components/LoadingSpinner'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { IconBrandStripe } from '@tabler/icons-react'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import {
  patchListCache,
  prependToList,
  removeFromList,
  replaceInList,
} from '@trainrr/api-client'
import {
  ExternalLink,
  Package as PackageIcon,
  Plus,
  Settings,
  Signature,
} from 'lucide-react'
import { useHasPermission, useOrg } from '@/lib/states/session/hooks'
import PackageDialog from '@/components/PackageDialog'
import createPackageColumns, {
  Package as PackageType,
} from '@/components/packages/packageColumns'
import PackageTermsDialog from '@/components/packages/PackageTermsDialog'
import DeleteAlert from '@/components/DeleteAlert'
import { toast } from 'sonner'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export const Route = createFileRoute('/_auth/_dashboard/packages')({
  beforeLoad: () => {
    requirePermission('packages.view')
  },
  component: PackagesPage,
  pendingComponent: () => <LoadingSpinner />,
})

function PackagesPage() {
  const [isConnecting, setIsConnecting] = useState(false)
  const canEditPackages = useHasPermission('packages.edit')

  const [showDialog, setShowDialog] = useState(false)
  const [showTermsDialog, setShowTermsDialog] = useState(false)
  const [editingPackage, setEditingPackage] = useState<PackageType | null>(null)
  const org = useOrg()
  const navigate = useNavigate()
  const { data: entitlements } = useOrgEntitlements()
  const stripeConnectLockedToGrowth =
    entitlements?.plan_key === 'starter_monthly'
  const hostedBase =
    import.meta.env.VITE_HOSTED_PLANS_URL || 'https://plans.trainrr.app'
  const canUsePublicIdentifier = entitlements?.plan_key !== 'starter_monthly'
  const hostedUrl =
    canUsePublicIdentifier && org?.slug
      ? `${hostedBase}/${org.slug}`
      : org?.id
        ? `${hostedBase}/${org.id}`
        : hostedBase

  const utils = trpc.useUtils()
  const archivePackage = trpc.packages.archive.useMutation()
  const { mutateAsync: setActive } = trpc.packages.setActive.useMutation()
  const { mutateAsync: setVisible } = trpc.packages.setVisible.useMutation()
  const togglingIdsRef = useRef(new Set<string>())
  const {
    data: packagesData,
    error: packagesError,
    isLoading: packagesLoading,
  } = trpc.packages.list.useQuery(undefined, { enabled: Boolean(org?.id) })

  useEffect(() => {
    if (packagesError) {
      console.error('Error loading packages:', packagesError)
      errorToast(new Error('Failed to load packages'))
    }
  }, [packagesError])

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [toDelete, setToDelete] = useState<PackageType | null>(null)

  const handleDelete = async () => {
    if (!canEditPackages) return
    if (!toDelete) return
    try {
      await archivePackage.mutateAsync({ packageId: toDelete.id })
      patchListCache(utils.packages.list, (old) =>
        removeFromList(old, toDelete.id),
      )
      toast.success('Package archived')
      setDeleteOpen(false)
      setToDelete(null)
    } catch (err) {
      console.error('Error deleting package:', err)
      errorToast(err as Error, 'Failed to delete package')
    }
  }

  const handleToggleActive = useCallback(
    async (pkg: PackageType, newVal: boolean) => {
      const toggleKey = `${pkg.id}:active`
      if (!canEditPackages || togglingIdsRef.current.has(toggleKey)) return
      togglingIdsRef.current.add(toggleKey)
      patchListCache(utils.packages.list, (old) =>
        old.map((row) =>
          row.id === pkg.id
            ? {
                ...row,
                is_active: newVal,
                ...(newVal ? {} : { is_visible: false }),
              }
            : row,
        ),
      )
      try {
        const updated = await setActive({
          packageId: pkg.id,
          isActive: newVal,
        })
        patchListCache(utils.packages.list, (old) => replaceInList(old, updated))
        toast.success('Package updated')
      } catch (err) {
        patchListCache(utils.packages.list, (old) => replaceInList(old, pkg))
        console.error('Failed to update package active:', err)
        errorToast(err as Error, 'Failed to update package')
      } finally {
        togglingIdsRef.current.delete(toggleKey)
      }
    },
    [canEditPackages, setActive, utils.packages.list],
  )

  const handleToggleVisible = useCallback(
    async (pkg: PackageType, newVal: boolean) => {
      const toggleKey = `${pkg.id}:visible`
      if (!canEditPackages || togglingIdsRef.current.has(toggleKey)) return
      togglingIdsRef.current.add(toggleKey)
      patchListCache(utils.packages.list, (old) =>
        old.map((row) =>
          row.id === pkg.id ? { ...row, is_visible: newVal } : row,
        ),
      )
      try {
        const updated = await setVisible({
          packageId: pkg.id,
          isVisible: newVal,
        })
        patchListCache(utils.packages.list, (old) => replaceInList(old, updated))
        toast.success('Package visibility updated')
      } catch (err) {
        patchListCache(utils.packages.list, (old) => replaceInList(old, pkg))
        console.error('Failed to update package visible:', err)
        errorToast(err as Error, 'Failed to update package')
      } finally {
        togglingIdsRef.current.delete(toggleKey)
      }
    },
    [canEditPackages, setVisible, utils.packages.list],
  )

  const { data } = trpc.org.get.useQuery(undefined, {
    enabled: Boolean(org?.id),
  })
  const { data: stripeStatus, isLoading: stripeStatusLoading } =
    trpc.billing.connectStatus.useQuery(undefined, {
      enabled: Boolean(org?.id),
    })
  const createOnboardingLink =
    trpc.billing.createConnectOnboardingLink.useMutation()

  const handleConnect = async () => {
    if (stripeConnectLockedToGrowth) {
      toast.error('Upgrade to Growth to connect Stripe payments.')
      await navigate({
        to: '/upgrade',
        search: { plan: 'growth_monthly' },
      })
      return
    }

    try {
      setIsConnecting(true)

      const result = await createOnboardingLink.mutateAsync({
        redirectUrl: window.location.href,
      })

      if (result.url) {
        window.location.href = result.url
        return
      }

      throw new Error('No URL returned from onboarding')
    } catch (err) {
      console.error('Stripe onboarding error:', err)
      errorToast(err as Error, 'Error connecting to Stripe')
    } finally {
      setIsConnecting(false)
    }
  }

  const renderStripeState = () => {
    if (stripeConnectLockedToGrowth) {
      return (
        <Button size={'sm'} className="gap-2" onClick={handleConnect}>
          <IconBrandStripe className="size-4" /> Upgrade to connect Stripe
        </Button>
      )
    }

    const stripeLabel = !data?.stripe_account_id
      ? 'Connect to Stripe'
      : stripeStatusLoading
        ? 'Stripe status loading...'
        : !stripeStatus?.details_submitted
          ? 'Continue Setup'
          : !stripeStatus.charges_enabled
            ? 'Verification pending'
            : 'Payments enabled'

    const stripeVariant =
      !data?.stripe_account_id ||
      (stripeStatus && !stripeStatus.details_submitted)
        ? 'default'
        : 'outline'

    return (
      <Button
        size={'sm'}
        className="gap-2"
        variant={stripeVariant}
        onClick={handleConnect}
        disabled={isConnecting || stripeStatusLoading}
      >
        {isConnecting ? (
          'Connecting...'
        ) : (
          <>
            <IconBrandStripe className="size-4" /> {stripeLabel}
          </>
        )}
      </Button>
    )
  }

  const packageColumns = useMemo(
    () =>
      createPackageColumns({
        canEdit: canEditPackages,
        onToggleActive: canEditPackages ? handleToggleActive : undefined,
        onToggleVisible: canEditPackages ? handleToggleVisible : undefined,
        orgRef: canUsePublicIdentifier ? org?.slug : org?.id,
      }),
    [
      canEditPackages,
      canUsePublicIdentifier,
      handleToggleActive,
      handleToggleVisible,
      org?.id,
      org?.slug,
    ],
  )

  return (
    <>
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">Packages</h2>
              <div className="ml-4 flex items-center gap-2">
                {renderStripeState()}
                {canEditPackages && (
                  <Button
                    onClick={() => {
                      setEditingPackage(null)
                      setShowDialog(true)
                    }}
                    className="gap-2"
                    size={'sm'}
                  >
                    <Plus className="h-4 w-4" />
                    New package
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label="Package settings"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to={hostedUrl} target="_blank">
                        <ExternalLink className="size-4" />
                        View hosted page
                      </Link>
                    </DropdownMenuItem>
                    {canEditPackages && (
                      <DropdownMenuItem
                        onClick={() => setShowTermsDialog(true)}
                      >
                        <Signature className="size-4" />
                        Terms and conditions
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {canEditPackages && (
              <PackageDialog
                open={showDialog}
                onOpenChange={(open) => setShowDialog(open)}
                initial={editingPackage ?? undefined}
                onSaved={(pkg) => {
                  setShowDialog(false)
                  setEditingPackage(null)
                  patchListCache(utils.packages.list, (old) =>
                    prependToList(old, pkg),
                  )
                }}
              />
            )}

            {/* delete confirmation dialog */}
            <DeleteAlert
              open={deleteOpen}
              title={toDelete ? `Delete ${toDelete.name}` : 'Delete package'}
              description={
                toDelete
                  ? `Are you sure you want to delete the package '${toDelete.name}'? This action cannot be undone.`
                  : 'Are you sure you want to delete this package?'
              }
              onCancel={() => {
                setDeleteOpen(false)
                setToDelete(null)
              }}
              onDelete={handleDelete}
            />

            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={packageColumns}
              data={packagesData ?? []}
              getRowId={(row) => (row as PackageType).id}
              stickyHeader
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              virtualizedHeight="100%"
              estimateRowHeight={72}
              emptyView={
                <Empty className="border border-dashed">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <PackageIcon className="h-5 w-5" />
                    </EmptyMedia>
                    <EmptyTitle>No packages yet</EmptyTitle>
                    <EmptyDescription>
                      Create your first package to start selling plans to
                      clients.
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    {canEditPackages && (
                      <Button type="button" onClick={() => setShowDialog(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Create package
                      </Button>
                    )}
                  </EmptyContent>
                </Empty>
              }
              showSearchBar={false}
              isLoading={!!packagesLoading}
              onRowSelected={
                canEditPackages
                  ? (pkg) => {
                      setEditingPackage(pkg as PackageType)
                      setShowDialog(true)
                    }
                  : undefined
              }
              onRowDeleted={
                canEditPackages
                  ? (pkg) => {
                      setToDelete(pkg as PackageType)
                      setDeleteOpen(true)
                    }
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      {canEditPackages && (
        <PackageTermsDialog
          open={showTermsDialog}
          onOpenChange={setShowTermsDialog}
        />
      )}
    </>
  )
}

export default PackagesPage
